import { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Org } from "../models/Org";
import { User } from "../models/User";
import {
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Backdrop,
  TableSortLabel,
  Box,
  CssBaseline,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Link, useParams } from "react-router-dom";
import { GroupAdd } from "@mui/icons-material";
import { loadRecords } from "../api/connection";
import Minibar from "../components/Minibar";
import PageHeader from "../components/PageHeader";
import { getRoleLabel } from "../utils/UserRoles";

export default function UsersPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [organizations, setOrganizations] = useState<Org[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Org | null>(
    null
  );
  const [users, setUsers] = useState<User[]>([]);
  const [isOrganizationSelected, setIsOrganizationSelected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof User | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });

  useEffect(() => {
    async function fetchUsersAndOrganizations(
      organizationId: string | undefined
    ) {
      setLoading(true);
      const orgs = await loadRecords<Org>("organizations");
      const sortedOrgs = orgs.sort((a, b) => a._id.localeCompare(b._id));
      setOrganizations(sortedOrgs);
      if (organizationId) {
        const selectedOrg =
          sortedOrgs.find((org) => org._id === organizationId) || null;
        setCurrentOrganization(selectedOrg);
        setIsOrganizationSelected(true);
        console.log("Set Current Organization:", selectedOrg);
      } else {
        setCurrentOrganization(sortedOrgs[0]);
        console.log("Set Current Organization:", sortedOrgs[0]);
      }
      console.log("Fetched Organizations:", sortedOrgs);
      console.log("Set Current Organization:", sortedOrgs[0]);
      const u: User[] = await loadRecords<User>(
        "users",
        ...(orgId ? ["orgId", orgId] : [])
      );
      setUsers(u);
      console.log("Fetched Users:", u);
      setLoading(false);
    }
    fetchUsersAndOrganizations(orgId);
  }, [orgId]);

  const handleCheckboxChange = () => {
    const newIsSelected = !isOrganizationSelected;
    setIsOrganizationSelected(newIsSelected);

    const sortedOrgs = organizations.sort((a, b) => a._id.localeCompare(b._id));
    setCurrentOrganization(sortedOrgs[0]);
  };

  const handleOrganizationChange = (event: SelectChangeEvent) => {
    const orgId = event.target.value as string;
    const selectedOrg = organizations.find((org) => org._id === orgId) || null;
    setCurrentOrganization(selectedOrg);
  };

  const filteredUsers = users
    .filter((user) => {
      if (!isOrganizationSelected) return true;
      const orgId = currentOrganization?._id || "";
      return user.orgId === orgId;
    })
    .filter((user) => {
      const searchTerm = filter.toLowerCase();
      return (
        user.name.toLowerCase().includes(searchTerm) ||
        user.tenantEmail.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm)
      );
    });

  const handleSort = (key: keyof User) => {
    const isSameColumn = sortConfig.key === key;
    const direction =
      isSameColumn && sortConfig.direction === "asc" ? "desc" : "asc";

    setSortConfig({ key, direction });

    const sortedUsers = [...users].sort((a, b) => {
      const aValue = a[key] ?? "";
      const bValue = b[key] ?? "";

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setUsers(sortedUsers);
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* page header */}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            m: 0,
            backgroundColor: "#212121",
            color: "#ECECEC",
            minHeight: "100vh",
          }}
        >
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          {!loading && currentOrganization && (
            <PageHeader
              title={`Users${
                isOrganizationSelected ? " of " + currentOrganization.name : ""
              }`}
              rightComponent={[
                <Minibar
                  minibarItems={[
                    ...(!orgId
                      ? [
                          {
                            component: (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isOrganizationSelected}
                                    onChange={handleCheckboxChange}
                                    sx={{
                                      "&.Mui-checked": {
                                        color: "#ADD8E6",
                                      },
                                    }}
                                  />
                                }
                                label={
                                  isOrganizationSelected
                                    ? "Show all Users"
                                    : "Select an Account"
                                }
                              />
                            ),
                            text: isOrganizationSelected
                              ? "Show all Users"
                              : "Select an Account",
                            isCheckBox: true,
                            checked: isOrganizationSelected,
                            // onClick: () => setIsOrganizationSelected(!isOrganizationSelected)
                            onClick: handleCheckboxChange,
                          },
                        ]
                      : []),
                    {
                      text: !isOrganizationSelected
                        ? "Select an account to add a new user"
                        : "Add New User",
                      icon: <GroupAdd />,
                      nav: `/edit-user/${currentOrganization?._id}/new`,
                      disabled: !isOrganizationSelected,
                    },
                  ]}
                  barLength={2}
                  searchLabel="Search Users"
                  onSearch={(e) => setFilter(e)}
                />,
              ]}
              // breadcrumbElements={[
              //   {
              //     label: `Dashboard`,
              //     href: "/dashboard",
              //   },
              //   ...(orgId
              //     ? [
              //         {
              //           label: `Organizations`,
              //           href: `/orgs/${orgId}`,
              //         },
              //       ]
              //     : []),
              //   {
              //     label: "Users",
              //     href: "",
              //   },
              // ]}
            />
          )}
          {isOrganizationSelected && !orgId && (
            <Grid container alignItems="center" spacing={2} padding={2}>
              <Grid size={12}>
                <Select
                  value={currentOrganization?._id || ""}
                  onChange={handleOrganizationChange}
                  fullWidth
                  style={{ textAlign: "left" }}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org._id} value={org._id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          )}

          <TableContainer>
            <Table sx={{ minWidth: 350 }} aria-label="user table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 3 }} width={"25%"}>
                    Name
                  </TableCell>
                  <TableCell align="left" width={"25%"}>
                    Email
                  </TableCell>
                  <TableCell align="left" width={"25%"}>
                    Tenant Email
                  </TableCell>
                  <TableCell align="left" width={"25%"}>
                    <TableSortLabel
                      active={sortConfig.key === "role"}
                      direction={
                        sortConfig.key === "role" ? sortConfig.direction : "asc"
                      }
                      onClick={() => handleSort("role")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      Role
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow hover key={user._id}>
                    <TableCell sx={{ pl: 3 }}>
                      <Link
                        style={{ textDecoration: "none", color: "#b0d1ff" }}
                        to={`/edit-user/${user.orgId}/${user._id}`}
                      >
                        {user.name}
                      </Link>
                    </TableCell>
                    <TableCell align="left">{user.email}</TableCell>
                    <TableCell align="left">{user.tenantEmail}</TableCell>
                    <TableCell align="left">
                      {getRoleLabel(user.role)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
}
